// src/components/DataManagement.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { 
  SectionContainer, 
  LayerCard, 
  SmoothSlider 
} from "./DataManagementComponents";

const DEFAULT_MIN_NDVI = -1.0;
const DEFAULT_MAX_NDVI = 0.93;
const PHOTO_CONTROL_LAYER_ID = "ground_truth_photos_control";

/* ----------------- Main DataManagement component ----------------- */
export default function DataManagement({ initial = null }) {
  // 1. Initialize State with userVectors bucket
  const seed = initial ?? {
    raster2d: [],
    raster3d: [],
    ndvi: [], 
    lidar: [],
    vectors: [],
    userVectors: [], // Stores user drawings
    photos: [],
    groundImages: [],
  };

  const [data, setData] = useState(seed);
   
  // 2. Set default open sections
  const [openSections, setOpenSections] = useState({
    userVectors: true,
    raster2d: true,
    raster3d: false,
    ndvi: false,
    vectors: false,
    photos: false,
    lidar: true,
    groundImages: true,
  });

  const [ndviParams, setNdviParams] = useState({ min: DEFAULT_MIN_NDVI, max: DEFAULT_MAX_NDVI });
  const [ndviHoverVal, setNdviHoverVal] = useState(null);
  const legendCanvasRef = useRef(null);

  const [currentSurvey, setCurrentSurvey] = useState(
    () => localStorage.getItem("survey_time") || ""
  );

  const [organization, setOrganization] = useState(
    () => localStorage.getItem("organization") || ""
  );
   
  const [locationName, setLocationName] = useState(() =>
    (localStorage.getItem("location_name") || "").trim()
  );

  const API_BASE = (
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
    process.env.REACT_APP_API_BASE ||
    "/api" // This fallback is the key to routing to the backend
  ).replace(/\/+$/, "");
   
  const [individualPhotoVisibility, setIndividualPhotoVisibility] = useState({});
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  // --- Upload Handler Functions ---
  const getLayerTypeMapping = (sectionKey) => {
    const mapping = {
      raster2d: { directory: "ortho", endpoint: "/layers", fieldName: "image_file", layerType: "raster2d" },
      raster3d: { directory: "3draster", endpoint: "/layers-upload", fieldName: "image_file", layerType: "raster3d" },
      ndvi: { directory: "ndvi", endpoint: "/layers-upload", fieldName: "image_file", layerType: "ndvi" },
      vectors: { directory: "geojson", endpoint: "/layers-upload", fieldName: "geojson_file", layerType: "vectors" },
      groundImages: { directory: "ground_img", endpoint: "/ground_mapx", fieldName: "file", layerType: "groundImages" },
      lidar: { directory: "lidar", endpoint: "/layers-upload", fieldName: "file", layerType: "lidar" },
      userVectors: { directory: "lidar", endpoint: "/layers-upload", fieldName: "file", layerType: "userVectors" },
    };
    return mapping[sectionKey] || { directory: "ortho", endpoint: "/layers-upload", fieldName: "file", layerType: "raster2d" };
  };

  const handleFileUpload = useCallback(async (sectionKey, files) => {
    if (!files || files.length === 0) return;

    const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
    const survey_time = clientNormalizeDate(currentSurvey || localStorage.getItem("survey_time") || "");
    
    if (!location_name) {
      alert("Please select a project/location first.");
      return;
    }

    if (!survey_time) {
      alert("Please select a survey date first.");
      return;
    }

    const layerName = prompt(`Enter a name for this ${sectionKey} layer:`);
    if (!layerName || !layerName.trim()) {
      return;
    }

    setUploading(prev => ({ ...prev, [sectionKey]: true }));

    try {
      const { directory, endpoint, fieldName, layerType } = getLayerTypeMapping(sectionKey);
      const token = localStorage.getItem("token") || "";
      
      const formData = new FormData();
      formData.append("location_name", location_name);
      formData.append("survey_time", survey_time);
      formData.append("layer_name", layerName.trim());
      
      // Add layer_type for unified upload endpoint
      if (endpoint === "/layers-upload") {
        formData.append("layer_type", layerType);
      }
      
      // Add all files
      Array.from(files).forEach((file) => {
        formData.append(fieldName, file);
      });

      // Add organization and email if available
      if (organization) formData.append("organization", organization);
      const userEmail = localStorage.getItem("user_email");
      if (userEmail) formData.append("email", userEmail);
      
      // Special handling for 3draster cesium
      if (sectionKey === "raster3d") {
        const useCesium = window.confirm("Is this a Cesium Ion asset? Click OK to add Cesium token.");
        if (useCesium) {
          const cesiumToken = prompt("Enter Cesium Ion Asset Key:");
          if (cesiumToken && cesiumToken.trim()) {
            formData.append("cesium_token", cesiumToken.trim());
          }
        }
      }

      const baseUrl = API_BASE || window.location.origin;
      const url = `${baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      console.log(`[Upload] Sending request to: ${url}`);

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          const text = await response.text().catch(() => "");
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      alert(`Files uploaded successfully to ${directory}/${location_name}/${survey_time}/${layerName}`);
      
      // Reset file input
      if (fileInputRefs.current[sectionKey]) {
        fileInputRefs.current[sectionKey].value = "";
      }
      
      // Trigger refresh by calling discovery endpoints and then emitting events
      setTimeout(async () => {
        const baseUrl = API_BASE || window.location.origin;
        const params = new URLSearchParams({
          location_name: location_name,
          survey_time: survey_time,
        });

        try {
          if (sectionKey === "raster2d") {
            const discoveryUrl = `${baseUrl}/layers/tiles-discovery?${params.toString()}`;
            const discoveryRes = await fetch(discoveryUrl);
            if (discoveryRes.ok) {
              const discoveryData = await discoveryRes.json();
              const found = discoveryData.found || [];
              const layerNames = found.map(l => l.layer_slug || l.layer_name);
              
              emitMapEvent("layers-discovered", { 
                layer_names: layerNames,
                location_name,
                survey_time,
                candidates: found // FIX: Pass full candidates
              });
            }
          } else if (sectionKey === "raster3d") {
            const discoveryUrl = `${baseUrl}/layers3d/tiles-discovery?${params.toString()}`;
            const discoveryRes = await fetch(discoveryUrl);
            if (discoveryRes.ok) {
              const discoveryData = await discoveryRes.json();
              const found = discoveryData.found || [];
              const layerNames = found.map(l => l.layer_slug || l.layer_name);

              emitMapEvent("raster3d-layers-discovered", { 
                layer_names: layerNames,
                location_name,
                survey_time,
                candidates: found // FIX: Pass full candidates
              });
            }
          } else if (sectionKey === "ndvi") {
            const discoveryUrl = `${baseUrl}/ndvi/tiles-discovery?${params.toString()}`;
            const discoveryRes = await fetch(discoveryUrl);
            if (discoveryRes.ok) {
              const discoveryData = await discoveryRes.json();
              const found = discoveryData.found || [];
              const layerNames = found.map(l => l.layer_id || l.layer_name || l.layer_slug);

              emitMapEvent("ndvi-layers-discovered", { 
                layer_names: layerNames,
                location_name,
                survey_time,
                candidates: found // FIX: Pass full candidates
              });
            }
          } else if (sectionKey === "vectors") {
            const discoveryUrl = `${baseUrl}/geojson/discovery?${params.toString()}`;
            const discoveryRes = await fetch(discoveryUrl);
            if (discoveryRes.ok) {
              const discoveryData = await discoveryRes.json();
              const fullLayers = Array.isArray(discoveryData.found) 
                ? discoveryData.found.map((cand, idx) => ({
                    id: `vector-${location_name}-${survey_time}-${cand.layer_name || cand.name || idx}`,
                    name: cand.layer_name || cand.name || `Vector ${idx + 1}`,
                    url: cand.url || cand.geojson_url,
                  }))
                : [];
              emitMapEvent("vector-layers-discovered", { 
                full_layers: fullLayers,
                location: location_name,
                survey_time 
              });
            }
            emitMapEvent("survey-selected", {
              location_name,
              survey_time,
              survey_date: survey_time,
            });
          } else if (sectionKey === "lidar") {
            const discoveryUrl = `${baseUrl}/lidar/tiles-discovery?${params.toString()}`;
            const discoveryRes = await fetch(discoveryUrl);
            if (discoveryRes.ok) {
              const discoveryData = await discoveryRes.json();
              const layers = Array.isArray(discoveryData) 
                ? discoveryData.map((item, idx) => ({
                    id: `lidar-${location_name}-${survey_time}-${item.name || idx}`,
                    name: item.name || `LiDAR ${idx + 1}`,
                    url_template: item.url_template,
                  }))
                : [];
              emitMapEvent("lidar-layers-discovered", {
                layers: layers,
                location: location_name,
                survey_time
              });
            }
          } else if (sectionKey === "groundImages") {
            emitMapEvent("survey-selected", {
              location_name,
              survey_time,
              survey_date: survey_time,
            });
          }
          
          if (sectionKey === "raster3d") {
            setTimeout(() => {
              emitMapEvent("survey-selected", {
                location_name,
                survey_time,
                survey_date: survey_time,
              });
            }, 200);
          }
        } catch (discoveryError) {
          console.error(`[Upload] Discovery error for ${sectionKey}:`, discoveryError);
          // Fallbacks with single name
          if (sectionKey === "raster2d") {
            emitMapEvent("layers-discovered", { layer_names: [layerName], location_name, survey_time });
          } else if (sectionKey === "raster3d") {
            emitMapEvent("raster3d-layers-discovered", { layer_names: [layerName], location_name, survey_time });
          } else if (sectionKey === "ndvi") {
            emitMapEvent("ndvi-layers-discovered", { layer_names: [layerName], location_name, survey_time });
          }
        }
      }, 100); 
    } catch (error) {
      console.error("Upload error:", error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [sectionKey]: false }));
    }
  }, [locationName, currentSurvey, organization, API_BASE]);

  const handleFileSelect = useCallback((sectionKey, event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileUpload(sectionKey, files);
    }
  }, [handleFileUpload]);

  const triggerFileInput = useCallback((sectionKey) => {
    if (fileInputRefs.current[sectionKey]) {
      fileInputRefs.current[sectionKey].click();
    }
  }, []);

  // --- Helper Functions ---
  const mapDbRowToUiLayer = (row) => {
    if (!row) return null;
    const id = row.id ?? `layer_${Date.now()}`;
    const title = row.layer_name ?? `Layer ${id}`;
    const visible = row.visible !== false;
    const opacity = row.opacity ?? 1;
    const image_url =
      row.image_url ||
      (row.image_file
        ? `${API_BASE}/${String(row.image_file).replace(/^\/+/, "")}`
        : null);
    return {
      id: String(id),
      title,
      visible,
      opacity,
      meta: {
        crs: {
          type: row.crs_type || "Geographic",
          datum: row.crs_datum || "WGS84",
          zone: row.crs_zone || "",
        },
        uploader: row.email || row.uploader || "unknown",
        notes: row.notes || null,
        image_url,
      },
    };
  };

  const emitMapEvent = (name, payload) => {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: payload }));
    } catch (e) {
      const ev = document.createEvent ? document.createEvent("CustomEvent") : null;
      if (ev && ev.initCustomEvent) {
        ev.initCustomEvent(name, true, true, payload);
        window.dispatchEvent(ev);
      }
    }
  };

  /* ----------------- Event Listeners & Effects ----------------- */

  useEffect(() => {
    const onStorage = (ev) => {
      if (!ev) return;
      if (ev.key === "location_name") setLocationName((ev.newValue || "").trim());
      if (ev.key === "organization") setOrganization(ev.newValue || "");
      if (ev.key === "survey_time") setCurrentSurvey(ev.newValue || "");
    };
    window.addEventListener("storage", onStorage);

    const onSurveySelected = (ev) => {
      const payload = ev?.detail ?? ev;
      
      const loc = payload?.location_name ?? payload?.location ?? payload?.project_name ?? null;
      if (loc) {
        const s = String(loc).trim();
        try { localStorage.setItem("location_name", s); } catch (e) {}
        setLocationName(s);
      }

      const rawTime = payload?.survey_time ?? payload?.survey_date ?? payload?.time ?? null;
      if (rawTime) {
         const norm = clientNormalizeDate(rawTime);
         try { localStorage.setItem("survey_time", norm); } catch (e) {}
         setCurrentSurvey(norm);
      }
    };

    window.addEventListener("survey-selected", onSurveySelected);
    window.addEventListener("survey-request-created", onSurveySelected);
    window.addEventListener("survey-request-updated", onSurveySelected);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("survey-selected", onSurveySelected);
      window.removeEventListener("survey-request-created", onSurveySelected);
      window.removeEventListener("survey-request-updated", onSurveySelected);
    };
  }, []);

  // --- NEW: Listen for User Vectors Discovered ---
  useEffect(() => {
    const onUserVectorsDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const layers = detail.layers || [];
      
      const formattedLayers = layers.map(l => ({
        id: l.id,
        title: l.name || l.id,
        visible: true, 
        opacity: 1,
        meta: { ...l.meta, isUserVector: true }
      }));

      setData(prev => ({ ...prev, userVectors: formattedLayers }));
    };

    window.addEventListener("user-vectors-discovered", onUserVectorsDiscovered);
    return () => window.removeEventListener("user-vectors-discovered", onUserVectorsDiscovered);
  }, []);

  // --- FIXED: Ortho (Raster2D) Discovery Listener with Index ---
  useEffect(() => {
    const onLayersDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const names = detail?.layer_names ?? [];
      
      // Use full candidates if available (robust), else map names
      const candidates = detail.candidates || names.map(n => ({ layer_name: n }));

      if (candidates.length === 0) {
        setData((prev) => ({
          ...prev,
          raster2d: (prev.raster2d || []).filter(
            (layer) => !String(layer.id).startsWith("auto-ortho-")
          ),
        }));
        return;
      }

      const newOrthoLayers = candidates.map((cand, index) => {
        const name = cand.layer_slug || cand.layer_name || cand.name || `Layer ${index}`;
        // FIX: Include index in ID to handle multiple layers with same name/slug
        const uniqueId = `auto-ortho-${name}-${index}`;
        
        return {
          id: uniqueId, 
          title: name, 
          visible: true, 
          opacity: 0.85,
          meta: {
            location_name: detail.location_name,
            survey_time: detail.survey_time,
            isAutoDiscovered: true,
          },
        };
      });

      setData((prev) => {
        const existingUserLayers = (prev.raster2d || []).filter(
          (layer) => !String(layer.id).startsWith("auto-ortho-")
        );
        // We replace all auto-discovered layers with the new set
        return {
          ...prev,
          raster2d: [...newOrthoLayers, ...existingUserLayers],
        };
      });
    };
    window.addEventListener("layers-discovered", onLayersDiscovered);
    return () => window.removeEventListener("layers-discovered", onLayersDiscovered);
  }, []);

  // --- FIXED: Raster3D Discovery Listener with Index ---
  useEffect(() => {
    const onRaster3dLayersDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const names = detail?.layer_names ?? [];
      const candidates = detail.candidates || names.map(n => ({ layer_name: n }));

      if (candidates.length === 0) {
        setData((prev) => ({
          ...prev,
          raster3d: (prev.raster3d || []).filter(
            (layer) => !String(layer.id).startsWith("auto-raster3d-")
          ),
        }));
        return;
      }

      const newRaster3dLayers = candidates.map((cand, index) => {
        const name = cand.layer_slug || cand.layer_name || cand.name || `3D Layer ${index}`;
        // FIX: Include index for Uniqueness
        const uniqueId = `auto-raster3d-${name}-${index}`;

        return {
          id: uniqueId,
          title: name,
          visible: true,
          opacity: 0.85,
          meta: {
            location_name: detail.location_name,
            survey_time: detail.survey_time,
            isAutoDiscovered: true,
          },
        };
      });

      setData((prev) => {
        const existingUserLayers3D = (prev.raster3d || []).filter(
          (layer) => !String(layer.id).startsWith("auto-raster3d-")
        );
        return {
          ...prev,
          raster3d: [...newRaster3dLayers, ...existingUserLayers3D],
        };
      });
    };
    window.addEventListener("raster3d-layers-discovered", onRaster3dLayersDiscovered);
    return () => window.removeEventListener("raster3d-layers-discovered", onRaster3dLayersDiscovered);
  }, []);

  // --- FIXED: NDVI Discovery Listener with Index ---
  useEffect(() => {
    const onNdviDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const names = detail?.layer_names ?? [];
      const candidates = detail.candidates || names.map(n => ({ layer_name: n }));

      if (candidates.length === 0) {
        setData((prev) => ({
          ...prev,
          ndvi: (prev.ndvi || []).filter((l) => !String(l.id).startsWith("auto-ndvi-")),
        }));
        return;
      }

      setData((prev) => {
        const existingLayersMap = new Map((prev.ndvi || []).map(l => [l.id, l]));
        
        const newNdviLayers = candidates.map((cand, index) => {
          const name = cand.layer_slug || cand.layer_name || cand.layer_id || `NDVI ${index + 1}`;
          // FIX: Use exact same ID pattern as NdviLayer.jsx to ensure controls work
          const id = `auto-ndvi-${name}-${index}`;
          
          const existing = existingLayersMap.get(id);
          return {
            id: id,
            title: name,
            visible: existing ? existing.visible : true, 
            opacity: existing ? existing.opacity : 0.85,
            params: existing ? existing.params : { min: DEFAULT_MIN_NDVI, max: DEFAULT_MAX_NDVI }, 
            meta: {
              location_name: detail.location_name,
              survey_time: detail.survey_time,
              isAutoDiscovered: true,
            },
          };
        });
        
        // Remove old auto layers, keep manual ones
        const existingUserLayers = (prev.ndvi || []).filter((l) => !String(l.id).startsWith("auto-ndvi-"));
        
        return { ...prev, ndvi: [...newNdviLayers, ...existingUserLayers] };
      });
    };
    window.addEventListener("ndvi-layers-discovered", onNdviDiscovered);
    return () => window.removeEventListener("ndvi-layers-discovered", onNdviDiscovered);
  }, []);

  useEffect(() => {
    const onHover = (e) => setNdviHoverVal(e.detail?.value ?? null);
    window.addEventListener("ndvi-hover-value", onHover);
    return () => window.removeEventListener("ndvi-hover-value", onHover);
  }, []);

  useEffect(() => {
    const onVectorsDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const candidates = detail?.full_layers ?? [];
      
      const newVectorLayers = candidates.map((cand) => ({
        id: cand.id, 
        title: cand.name,
        visible: cand.visible,
        opacity: cand.opacity,
        count: null,
        meta: {
          isAutoDiscovered: true,
          url: cand.url,
          location_name: detail.location,
          survey_time: detail.survey_time,
        },
      }));

      setData((prev) => {
        const existingLayers = (prev.vectors || []).filter((l) => {
          const isAuto = (l.meta && l.meta.isAutoDiscovered) || String(l.id).startsWith("vector-") || String(l.id).startsWith("auto-vector-");
          return !isAuto;
        });
        return { ...prev, vectors: [...newVectorLayers, ...existingLayers] };
      });
    };
    window.addEventListener("vector-layers-discovered", onVectorsDiscovered);
    return () => window.removeEventListener("vector-layers-discovered", onVectorsDiscovered);
  }, []);

  useEffect(() => {
    const onGroundImagesDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const candidates = Array.isArray(detail?.full_layers) ? detail.full_layers : [];
      const newImageLayers = candidates.map((cand) => ({
        id: cand.id,
        title: cand.title || cand.name || cand.itemname || cand.url,
        name: cand.name || cand.itemname || cand.title,
        visible: typeof cand.visible === "boolean" ? cand.visible : true,
        opacity: typeof cand.opacity === "number" ? cand.opacity : 1.0,
        count: null,
        meta: { ...(cand.meta || {}), isAutoDiscovered: true, url: cand.url, location_name: detail.location, survey_time: detail.survey_time },
      }));
      setData((prev) => {
        const prevGround = Array.isArray(prev.groundImages) ? prev.groundImages : [];
        const manualOnly = prevGround.filter((l) => {
          const isAuto = (l.meta && l.meta.isAutoDiscovered) || String(l.id).startsWith("groundimg-") || String(l.id).startsWith("auto-groundimg-");
          return !isAuto;
        });
        return { ...prev, groundImages: [...newImageLayers, ...manualOnly] };
      });
    };
    window.addEventListener("ground-images-discovered", onGroundImagesDiscovered);
    return () => window.removeEventListener("ground-images-discovered", onGroundImagesDiscovered);
  }, [setData]);

  useEffect(() => {
    const onLidarDiscovered = (ev) => {
      const detail = ev?.detail ?? {};
      const layers = Array.isArray(detail?.layers) ? detail.layers : [];
      const newLidarLayers = layers.map((layer) => ({
        id: layer.id,
        title: layer.name || layer.title || "LiDAR Layer",
        visible: layer.visible !== false,
        opacity: 1.0,
        count: null,
        meta: {
          isAutoDiscovered: true,
          url: layer.url,
          location_name: detail.location,
          survey_time: detail.survey_time
        }
      }));
      setData((prev) => {
        const prevLidar = Array.isArray(prev.lidar) ? prev.lidar : [];
        const manualOnly = prevLidar.filter((l) => {
          const isAuto = (l.meta && l.meta.isAutoDiscovered) || String(l.id).startsWith("lidar-");
          return !isAuto;
        });
        return { ...prev, lidar: [...newLidarLayers, ...manualOnly] };
      });
    };
    window.addEventListener("lidar-layers-discovered", onLidarDiscovered);
    return () => window.removeEventListener("lidar-layers-discovered", onLidarDiscovered);
  }, []);

  useEffect(() => {
    if (openSections.ndvi && legendCanvasRef.current) {
      const ctx = legendCanvasRef.current.getContext("2d");
      const w = legendCanvasRef.current.width;
      const h = legendCanvasRef.current.height;
      ctx.clearRect(0, 0, w, h);
      for (let x = 0; x < w; x++) {
        const t = x / w;
        const val = -1 + (t * 2); 
        ctx.fillStyle = getColorRamp(val);
        ctx.fillRect(x, 0, 1, h);
      }
    }
  }, [openSections.ndvi]);

  const updateLayerNdviParams = useCallback((layerId, newPartialParams) => {
    setData((prev) => {
      const updatedNdvi = prev.ndvi.map((layer) => {
        if (layer.id !== layerId) return layer;
        const mergedParams = { ...layer.params, ...newPartialParams };
        emitMapEvent("ndvi-set-params", { id: layer.id, ...mergedParams });
        return { ...layer, params: mergedParams };
      });
      return { ...prev, ndvi: updatedNdvi };
    });
  }, []);

  /* ----------------- Fetchers ----------------- */
  // ... (Fetchers remain mostly the same, ensuring database loaded layers don't conflict)
  // [Code truncated for brevity as fetchers logic is standard, focusing on event listeners above]

  // Full fetcher code from previous provided file is compatible.
  
  const fetchRaster3DLayers = useCallback(
    async (signal) => {
      try {
        const org = (organization || localStorage.getItem("organization") || "").trim();
        const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
        const survey_time = clientNormalizeDate(currentSurvey || "");

        if (!location_name) return [];

        const params = new URLSearchParams();
        if (org) params.set("organization", org);
        if (location_name) params.set("location_name", location_name);
        if (survey_time) params.set("survey_time", survey_time);

        const url = `${API_BASE}/layers3d${params.toString() ? `?${params.toString()}` : ""}`;
        const token = localStorage.getItem("token") || "";

        const res = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });

        if (!res.ok) return [];

        const json = await res.json().catch(() => null);
        const rows = Array.isArray(json) ? json : json?.layers ?? json?.data ?? [];

        if (!Array.isArray(rows) || rows.length === 0) return [];

        const filtered = rows.filter((r) => {
          const rLoc = (r.location_name ?? r.location ?? "").toString().trim();
          const rOrg = (r.organization ?? "").toString().trim();
          const rSurvey = clientNormalizeDate(r.survey_time ?? r.survey_date ?? r.survey ?? "") || "";

          if (!rLoc || rLoc !== location_name) return false;
          if (survey_time && (!rSurvey || rSurvey !== survey_time)) return false;
          if (org && (!rOrg || rOrg !== org)) return false;
          return true;
        });

        return filtered.map(mapDbRowToUiLayer).filter(Boolean);
      } catch (err) {
        if (err.name !== "AbortError") console.error("fetchRaster3DLayers error:", err);
        return [];
      }
    },
    [organization, currentSurvey, locationName, API_BASE]
  );

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
      if (!location_name) {
        setData((prev) => ({ 
          ...prev, 
          raster3d: (prev.raster3d || []).filter(l => !String(l.id).startsWith("auto-raster3d-"))
        }));
        return;
      }

      const fetched = await fetchRaster3DLayers(controller.signal);
      const activeLocation = (localStorage.getItem("location_name") || "").trim();
      const activeOrg = (localStorage.getItem("organization") || "").trim();
      const activeSurvey = clientNormalizeDate(currentSurvey || localStorage.getItem("survey_time") || "");
      
      if (controller.signal.aborted) return;

      if (Array.isArray(fetched)) {
        setData((prev) => {
          const existingAutoLayers3D = (prev.raster3d || []).filter((l) =>
            String(l.id).startsWith("auto-raster3d-")
          );
          // Use Titles for deduping if necessary, though IDs are safer
          const autoLayerNames3D = new Set(existingAutoLayers3D.map((l) => String(l.title)));
          const filteredFetched3D = fetched.filter((l) => !autoLayerNames3D.has(l.title));
          const nextRaster3d = [...existingAutoLayers3D, ...filteredFetched3D];

          (filteredFetched3D || []).forEach((layer) => {
            emitMapEvent("map-add-layer", {
              id: layer.id,
              visible: layer.visible,
              opacity: layer.opacity ?? 1,
              meta: layer.meta || {},
              location_name: layer.meta?.location_name || activeLocation,
              survey_time: layer.meta?.survey_time || activeSurvey,
              organization: layer.meta?.organization || activeOrg,
              zoomToLayer: false,
            });
          });
          return { ...prev, raster3d: nextRaster3d };
        });
      }
    })();
    return () => controller.abort();
  }, [organization, currentSurvey, locationName, fetchRaster3DLayers]);

  // ... (Rest of fetchers: fetchVectorLayers, fetchLidarLayers, fetchGroundPhotos - kept as is)
  const fetchVectorLayers = useCallback(
    async (signal) => {
      try {
        const org = (organization || localStorage.getItem("organization") || "").trim();
        const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
        const survey_time = clientNormalizeDate(currentSurvey || "");

        if (!location_name) return [];

        const params = new URLSearchParams();
        if (org) params.set("organization", org);
        if (location_name) params.set("location_name", location_name);
        if (survey_time) params.set("survey_time", survey_time);

        const url = `${API_BASE}/vectors${params.toString() ? `?${params.toString()}` : ""}`;
        const token = localStorage.getItem("token") || "";

        const res = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });

        if (!res.ok) return [];

        const json = await res.json().catch(() => null);
        const rows = Array.isArray(json) ? json : json?.vectors ?? json?.data ?? [];

        if (!Array.isArray(rows) || rows.length === 0) return [];

        const filtered = rows.filter((r) => {
          const rLoc = (r.location_name ?? r.location ?? "").toString().trim();
          const rOrg = (r.organization ?? "").toString().trim();
          const rSurvey = clientNormalizeDate(r.survey_time ?? r.survey_date ?? r.survey ?? "") || "";

          if (!rLoc || rLoc !== location_name) return false;
          if (survey_time && (!rSurvey || rSurvey !== survey_time)) return false;
          if (org && (!rOrg || rOrg !== org)) return false;
          return true;
        });

        return filtered.map((r) => {
          const id = String(r.id ?? `vector_${Date.now()}`);
          const title = r.layer_name || r.name || `Vector ${id}`;
          const visible = r.visible !== false;
          const count = typeof r.feature_count === "number" ? r.feature_count : Array.isArray(r.features) ? r.features.length : null;
          const geojson_url = r.geojson_url || (r.geojson_file ? `${API_BASE}/${String(r.geojson_file).replace(/^\/+/, "")}` : null);

          return {
            id,
            title,
            visible,
            count,
            meta: {
              crs: {
                type: r.crs_type || "Geographic",
                datum: r.crs_datum || "WGS84",
                zone: r.crs_zone || "",
              },
              uploader: r.email || r.uploader || "unknown",
              notes: r.notes || null,
              geojson_url,
              location_name: r.location_name || null,
              survey_time: clientNormalizeDate(r.survey_time ?? r.survey_date ?? r.survey ?? "") || null,
              organization: r.organization || null,
            },
          };
        });
      } catch (err) {
        if (err && err.name !== "AbortError") console.error("fetchVectorLayers error:", err);
        return [];
      }
    },
    [organization, currentSurvey, locationName, API_BASE]
  ); 

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
      if (!location_name) {
        setData((prev) => ({ ...prev, vectors: [] }));
        return;
      }

      try {
        const fetched = await fetchVectorLayers(controller.signal);
        if (!Array.isArray(fetched)) {
          setData((prev) => ({ ...prev, vectors: [] }));
          return;
        }
        setData((prev) => ({ ...prev, vectors: fetched }));
        emitMapEvent("map-vector-layers-loaded", {
          location_name: location_name, 
          layers: fetched, 
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        if (err && err.name !== "AbortError") console.error("vectors fetch effect error:", err);
      }
    })();
    return () => controller.abort();
  }, [organization, currentSurvey, locationName, fetchVectorLayers]);

  const fetchLidarLayers = useCallback(
    async (signal) => {
      try {
        const org = (organization || localStorage.getItem("organization") || "").trim();
        const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
        const survey_time = clientNormalizeDate(currentSurvey || "");

        if (!location_name) return [];

        const params = new URLSearchParams();
        if (org) params.set("organization", org);
        if (location_name) params.set("location", location_name);
        if (survey_time) params.set("survey_time", survey_time);

        const url = `${API_BASE}/lidar/tiles-discovery?${params.toString()}`;
        const token = localStorage.getItem("token") || "";

        const res = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });

        if (!res.ok) return [];

        const json = await res.json().catch(() => null);
        const rows = Array.isArray(json) ? json : [];

        return rows.map((r) => {
          const layerSlug = r.slug || r.name || "default";
          const id = `lidar-${location_name}-${survey_time}-${layerSlug}`;
          return {
            id: id,
            title: r.name || layerSlug,
            visible: true,
            opacity: 1.0, 
            count: null,
            meta: {
              isAutoDiscovered: true,
              url_template: r.url_template,
              location_name,
              survey_time
            }
          };
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("fetchLidarLayers error:", err);
        return [];
      }
    },
    [organization, currentSurvey, locationName, API_BASE]
  );

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
      if (!location_name) {
        setData((prev) => ({ ...prev, lidar: [] }));
        return;
      }
      const fetched = await fetchLidarLayers(controller.signal);
      if (Array.isArray(fetched)) {
        setData((prev) => ({ ...prev, lidar: fetched }));
      }
    })();
    return () => controller.abort();
  }, [organization, currentSurvey, locationName, fetchLidarLayers]);

  function mapDbRowToUiPhoto(row) {
    if (!row) return null;
    const xImg = row.x_img != null ? String(row.x_img).trim() : "";
    const yImg = row.y_img != null ? String(row.y_img).trim() : "";
    const xGrid = row.x_grid != null ? String(row.x_grid).trim() : "";
    const yGrid = row.y_grid != null ? String(row.y_grid).trim() : "";
    const lon = parseNumberLike(xImg) ?? null;
    const lat = parseNumberLike(yImg) ?? null;

    return {
      id: row.id ?? `photo_${Date.now()}`,
      title: row.photo_name ?? row.file_name ?? `photo_${row.id ?? Date.now()}`,
      uploader: row.email ?? row.uploader ?? null,
      organization: row.organization ?? null,
      location_name: row.location_name ?? null,
      survey_time: row.survey_time
        ? clientNormalizeDate(row.survey_time)
        : row.survey_date
        ? clientNormalizeDate(row.survey_date)
        : null,
      x_grid: xGrid,
      y_grid: yGrid,
      x_img: xImg,
      y_img: yImg,
      lon: Number.isFinite(lon) ? lon : null,
      lat: Number.isFinite(lat) ? lat : null,
      notes: row.notes ?? null,
      fileName: row.file_name ?? null,
      fileUrl: row.file_url ?? null,
      visible: true,
      pending: !!row.pending || false,
      raw: row,
    };
  }

  const fetchGroundPhotos = useCallback(
    async (signal) => {
      try {
        const org = (organization || localStorage.getItem("organization") || "").trim();
        const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
        const survey_time = clientNormalizeDate(currentSurvey || "");

        if (!location_name) return [];

        const params = new URLSearchParams();
        if (org) params.set("organization", org);
        if (location_name) params.set("location_name", location_name);
        if (survey_time) params.set("survey_time", survey_time);

        const url = `${API_BASE}/ground_mapx${params.toString() ? `?${params.toString()}` : ""}`;
        const token = localStorage.getItem("token") || "";

        const res = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal,
        });

        if (!res.ok) return [];

        const json = await res.json().catch(() => null);
        const rows = Array.isArray(json) ? json : json?.ground_mapx ?? json?.rows ?? json?.data ?? [];

        if (!Array.isArray(rows) || rows.length === 0) return [];

        const filtered = rows.filter((r) => {
          const rLoc = (r.location_name ?? r.location ?? "").toString().trim();
          const rOrg = (r.organization ?? "").toString().trim();
          const rSurvey = clientNormalizeDate(r.survey_time ?? r.survey_date ?? r.survey ?? "") || "";

          if (!rLoc || rLoc !== location_name) return false;
          if (survey_time && (!rSurvey || rSurvey !== survey_time)) return false;
          if (org && (!rOrg || rOrg !== org)) return false;
          return true;
        });

        return filtered.map(mapDbRowToUiPhoto).filter(Boolean);
      } catch (err) {
        if (err.name !== "AbortError") console.error("fetchGroundPhotos error:", err);
        return [];
      }
    },
    [organization, currentSurvey, locationName, API_BASE]
  );

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const location_name = (locationName || localStorage.getItem("location_name") || "").trim();
      if (!location_name) {
        setData((prev) => ({ ...prev, photos: [] }));
        return;
      }

      const fetched = await fetchGroundPhotos(controller.signal);

      if (Array.isArray(fetched)) {
        setData((prev) => {
          const controlLayer = prev.photos.find(
            (l) => l.id === PHOTO_CONTROL_LAYER_ID
          ) || {
            id: PHOTO_CONTROL_LAYER_ID,
            title: "Ground Truth Photos",
            visible: true,
            count: 0,
          };
          const isGlobalVisible = controlLayer.visible;

          let mapVisiblePhotos = [];
          const uiPhotosForSidebar = fetched.map((photo) => {
            const toggleId = `photo-${photo.id}`; 
            const isIndividualVisible = individualPhotoVisibility[toggleId] !== false;
            const finalVisibleOnMap = isGlobalVisible && isIndividualVisible;
            if (finalVisibleOnMap) mapVisiblePhotos.push(photo); 
            return {
              ...photo,
              id: toggleId, 
              visible: isIndividualVisible, 
              toggle_id: toggleId, 
            };
          });

          const nextPhotosLayer = [
            {
              ...controlLayer,
              count: fetched.length,
              title: `Ground Truth Photos (${fetched.length})`,
            },
            ...(isGlobalVisible ? uiPhotosForSidebar : []),
          ];

          setTimeout(() => {
            emitMapEvent("map-update-ground-photos", { photos: mapVisiblePhotos });
          }, 0); 

          return { ...prev, photos: nextPhotosLayer };
        });
      }
    })();
    return () => controller.abort();
  }, [organization, currentSurvey, locationName, fetchGroundPhotos]);

  const handleSectionToggle = useCallback((sectionKey) => {
    setOpenSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  // --- NEW: Delete User Vector ---
  const deleteUserVector = useCallback((id) => {
      if (!window.confirm("Delete this drawing?")) return;
      
      // Update UI
      setData(prev => ({
          ...prev,
          userVectors: prev.userVectors.filter(l => l.id !== id)
      }));
      
      // Tell Map to handle API
      emitMapEvent("map-remove-user-vector", { id });
  }, []);

  const toggleLayer = useCallback(
    (bucket, id) => {
      const isGlobalPhotoToggle = bucket === "photos" && id === PHOTO_CONTROL_LAYER_ID;
      const isIndividualPhotoToggle = bucket === "photos" && id !== PHOTO_CONTROL_LAYER_ID;

      setData((prev) => {
        if (isGlobalPhotoToggle) {
          const currentControlLayer = prev.photos.find((l) => l.id === PHOTO_CONTROL_LAYER_ID) || { visible: true }; 
          const newGlobalVisibleState = !currentControlLayer.visible;

          let newBucketState = prev.photos.map((l) =>
            l.id === PHOTO_CONTROL_LAYER_ID ? { ...l, visible: newGlobalVisibleState } : l
          );

          const allRawPhotos = prev.photos.filter((p) => p.id !== PHOTO_CONTROL_LAYER_ID).map((p) => p.raw || p);

          let photosToSendToMap = [];
          
          if (newGlobalVisibleState) {
            const resetVisibility = {};
            allRawPhotos.forEach((photo) => {
              const toggleId = `photo-${photo.id}`; 
              resetVisibility[toggleId] = true; 
            });
            setIndividualPhotoVisibility(resetVisibility); 
            photosToSendToMap = allRawPhotos; 
            newBucketState = [...newBucketState.filter(l => l.id === PHOTO_CONTROL_LAYER_ID), ...allRawPhotos.map(mapDbRowToUiPhoto)];
            
          } else {
            photosToSendToMap = [];
             newBucketState = newBucketState.filter(l => l.id === PHOTO_CONTROL_LAYER_ID);
          }

          emitMapEvent("map-update-ground-photos", { photos: photosToSendToMap });
          emitMapEvent("map-toggle-layer", { id: PHOTO_CONTROL_LAYER_ID, visible: newGlobalVisibleState });
          return { ...prev, [bucket]: newBucketState };

        } else if (isIndividualPhotoToggle) {
          const newIndividualVisibleState = !(individualPhotoVisibility[id] !== false);
          const updatedVisibilityState = { ...individualPhotoVisibility, [id]: newIndividualVisibleState };
          setIndividualPhotoVisibility(updatedVisibilityState);

          const newBucketState = prev.photos.map((l) =>
            l.id === id ? { ...l, visible: newIndividualVisibleState } : l
          );

          const controlLayer = prev.photos.find((l) => l.id === PHOTO_CONTROL_LAYER_ID);
          const isGlobalVisible = controlLayer?.visible !== false;

          let photosToSendToMap = [];
          if (isGlobalVisible) {
            photosToSendToMap = newBucketState
              .filter((photo) => {
                if (photo.id === PHOTO_CONTROL_LAYER_ID) return false;
                return updatedVisibilityState[photo.id] !== false;
              })
              .map((p) => p.raw || p); 
          }
          
          emitMapEvent("map-update-ground-photos", { photos: photosToSendToMap });
          return { ...prev, [bucket]: newBucketState };

        } else {
          // --- Generic Toggle (Works for userVectors too) ---
          const newBucketState = prev[bucket].map((l) => {
            if (l.id !== id) return l;
            const updated = { ...l, visible: !l.visible };
            emitMapEvent("map-toggle-layer", { id: updated.id, visible: !!updated.visible });
            return updated;
          });
          return { ...prev, [bucket]: newBucketState };
        }
      });
    },
    [individualPhotoVisibility] 
  );

  const setOpacity = useCallback((bucket, id, value) => {
    setData((prev) => {
      const newBucket = prev[bucket].map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, opacity: value };
        emitMapEvent("map-set-opacity", { id: updated.id, opacity: Number(value) });
        return updated;
      });
      return { ...prev, [bucket]: newBucket };
    });
  }, []);

  const removeLayer = useCallback((bucket, id) => {
    if (!window.confirm("Remove this layer?")) return;
    setData((prev) => {
      const next = { ...prev, [bucket]: prev[bucket].filter((l) => l.id !== id) };
      emitMapEvent("map-remove-layer", { id });
      return next;
    });
  }, []);

  function clientNormalizeDate(raw) {
    if (!raw) return "";
    try {
      if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      const d = new Date(raw);
      if (!isNaN(d.getTime())) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
      return String(raw);
    } catch {
      return String(raw);
    }
  }

  function parseNumberLike(s) {
    if (s == null) return null;
    if (typeof s === "number") return Number.isFinite(s) ? s : null;
    let str = String(s).trim();
    if (!str) return null;

    const fracMatch = str.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
    if (fracMatch) {
      const a = Number(fracMatch[1]), b = Number(fracMatch[2]);
      return Number.isFinite(a) && Number.isFinite(b) && b !== 0 ? a / b : null;
    }
    const hasDot = str.includes(".");
    const hasComma = str.includes(",");
    if (hasDot && hasComma) {
      if (str.lastIndexOf(".") > str.lastIndexOf(",")) str = str.replace(/,/g, "");
      else str = str.replace(/\./g, "").replace(/,/g, ".");
    } else if (hasComma && !hasDot) {
      const parts = str.split(",");
      if (parts.length === 2 && parts[1].length <= 3) str = `${parts[0]}.${parts[1]}`;
      else str = parts.join("");
    }
    str = str.replace(/\s+/g, "");
    const n = Number(str);
    return Number.isFinite(n) ? n : null;
  }

  const deletePhoto = async (id) => {
    if (!id) return;
    const numericIdMatch = String(id).match(/(\d+)$/);
    const numericId = numericIdMatch ? Number(numericIdMatch[1]) : 0;
    if (!Number.isFinite(numericId) || numericId <= 0) {
      alert("Cannot delete (local/unsaved or invalid ID).");
      return false; 
    }
    if (!window.confirm("Delete this photo? This cannot be undone.")) return false; 

    const token = localStorage.getItem("token") || "";
    const url = `${API_BASE.replace(/\/+$/, "")}/ground_mapx/${numericId}`;
    let removed = null;

    setData((prev) => {
      const before = prev?.photos || [];
      removed = before.find((p) => String(p.id) === String(id));
      const nextPhotos = before.filter((p) => String(p.id) !== String(id));
      return { ...prev, photos: nextPhotos };
    });
    setIndividualPhotoVisibility((prev) => {
      const next = { ...prev };
      delete next[id]; 
      return next;
    });

    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.status === 204 || res.status === 200) {
        emitMapEvent("map-remove-photo", { id }); 
        return true; 
      }
      if (res.status !== 404 && removed) {
        setData((prev) => ({ ...prev, photos: [removed, ...(prev.photos || [])] }));
        setIndividualPhotoVisibility((prevVis) => ({ ...prevVis, [id]: removed.visible }));
      }
      if (res.status === 401) return alert("Not authorized. Please login again.");
      if (res.status === 403) return alert("You are not allowed to delete this photo.");
      if (res.status === 404) return alert("Photo not found on server.");
      alert(`Failed to delete photo (server returned ${res.status}).`);
    } catch (err) {
      setData((prev) => ({ ...prev, photos: [...(removed ? [removed] : []), ...(prev.photos || [])] }));
      alert("Network error while deleting photo.");
    }
  };

  const sections = [
    { key: "raster2d", title: "2D RASTER LAYERS" },
    { key: "raster3d", title: "3D RASTER LAYERS" },
    { key: "ndvi", title: "MULTISPECTRAL ANALYSIS" },
    { key: "vectors", title: "VECTOR LAYERS" },
    { key: "groundImages", title: "GROUND TRUTH PHOTOS" },
    { key: "lidar", title: "LIDAR POINT CLOUD" },
    { key: "userVectors", title: "VECTOR DRAWINGS" },
  ];

  function getColorRamp(ndvi) {
    const v = Math.max(-1, Math.min(1, ndvi));
    const t = (v + 1) / 2;
    if (t < 0.5) {
      const f = t / 0.5;
      const r = 255;
      const g = Math.round(255 * f);
      return `rgba(${r}, ${g}, 0, 1)`;
    } else {
      const f = (t - 0.5) / 0.5;
      const r = Math.round(255 * (1 - f));
      const g = 255;
      return `rgba(${r}, ${g}, 0, 1)`;
    }
  }

  /* ----------------- Render ----------------- */
  return (
    <>
      <aside
        style={{
          width: 280,
          position: "sticky",
          top: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #07090cff 0%, #1a253a 100%)",
          color: "#f3f4f6",
          borderLeft: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Data Management</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 15 }}>
              Survey:&nbsp;
              <span style={{ color: "#e2e8f0", fontWeight: 600 }}>
                {formatSurveyLabel(currentSurvey)}
              </span>
            </div>
            <span style={{ color: "#9ca3af", fontSize: 11 }}>Org:</span>{" "}
            <span style={{ color: "#e2e8f0", fontSize: 11 }}>
              {organization || "—"}
            </span>
          </div>
        </div>

        <div style={{ padding: 10, overflowY: "auto", flex: "1 1 auto" }}>
          {sections.map(({ key, title }) => (
            <SectionContainer
              key={key}
              title={title}
              open={!!openSections[key]}
              onToggle={() => handleSectionToggle(key)}
            >
              {/* Upload Button */}
              <div style={{ 
                marginBottom: 12, 
                padding: "5px",
              }}>
                <input
                  ref={el => fileInputRefs.current[key] = el}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => handleFileSelect(key, e)}
                />
                <button
                  onClick={() => triggerFileInput(key)}
                  disabled={uploading[key]}
                  style={{
                    width: "100%",
                    padding: "7px 5px",
                    background: uploading[key] 
                      ? "rgba(99, 102, 241, 0.15)" 
                      : "rgba(99, 102, 241, 0.1)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    border: uploading[key] 
                      ? "1px solid rgba(99, 102, 241, 0.2)"
                      : "1px solid rgba(99, 102, 241, 0.25)",
                    borderRadius: 8,
                    color: uploading[key] ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.9)",
                    fontSize: 12,
                    fontWeight: 500,
                    letterSpacing: "0.3px",
                    cursor: uploading[key] ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: uploading[key] 
                      ? "none"
                      : "0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)",
                    transform: uploading[key] ? "scale(0.98)" : "scale(1)",
                    opacity: uploading[key] ? 0.6 : 1,
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading[key]) {
                      e.target.style.background = "rgba(99, 102, 241, 0.2)";
                      e.target.style.borderColor = "rgba(99, 102, 241, 0.4)";
                      e.target.style.color = "rgba(255,255,255,1)";
                      e.target.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255,255,255,0.15)";
                      e.target.style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading[key]) {
                      e.target.style.background = "rgba(99, 102, 241, 0.1)";
                      e.target.style.borderColor = "rgba(99, 102, 241, 0.25)";
                      e.target.style.color = "rgba(255,255,255,0.9)";
                      e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)";
                      e.target.style.transform = "translateY(0)";
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!uploading[key]) {
                      e.target.style.transform = "translateY(0) scale(0.97)";
                      e.target.style.background = "rgba(99, 102, 241, 0.25)";
                    }
                  }}
                  onMouseUp={(e) => {
                    if (!uploading[key]) {
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.background = "rgba(99, 102, 241, 0.2)";
                    }
                  }}
                >
                  {uploading[key] ? (
                    <>
                      <span style={{ fontSize: 14, opacity: 0.8 }}>⏳</span>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                        <span>Upload Files</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* --- SPECIAL RENDER FOR USER DRAWINGS --- */}
              {key === "userVectors" ? (
                  <>
                  {(data.userVectors || []).map((layer) => (
                    <LayerCard
                      key={layer.id}
                      bucket="userVectors"
                      layer={layer}
                      onToggle={(l) => toggleLayer("userVectors", l.id)}
                      onRemove={(l) => deleteUserVector(l.id)} 
                      onZoom={(l) => emitMapEvent("map-zoom-layer", { id: l.id })}
                    />
                  ))}
                  {(!data.userVectors || data.userVectors.length === 0) && (
                    <div style={{ padding: 10, textAlign: "center", color: "#64748b", fontSize: 12, fontStyle: "italic" }}>
                      No drawings found
                    </div>
                  )}
                  </>
              ) : key === "ndvi" ? (
              <>
                {(data.ndvi || []).map((layer) => (
                  <div
                    key={layer.id}
                    style={{
                      marginBottom: 12,
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.05)",
                    }}
                  >
                    <LayerCard
                      bucket="ndvi"
                      layer={layer}
                      onToggle={(l) => toggleLayer("ndvi", l.id)}
                      onOpacityChange={(val) => setOpacity("ndvi", layer.id, val)}
                      onRemove={(l) => removeLayer("ndvi", l.id)}
                      onZoom={(l) => emitMapEvent("map-zoom-layer", { id: l.id })}
                    />
                    {layer.visible && (
                      <div style={{ padding: "0 10px 12px 10px" }}>
                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                           <span style={{fontSize: 10, color: '#9ca3af', minWidth: 30}}>Opac</span>
                           <SmoothSlider 
                             value={layer.opacity ?? 1} 
                             onChange={(val) => setOpacity("ndvi", layer.id, val)} 
                           />
                        </div>
                        <hr style={{border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', margin: '8px 0'}} />
                        
                        {/* Gradient Legend */}
                        <div style={{ marginBottom: 12 }}>
                           <div style={{ height: 10, width: '100%', borderRadius: 2, background: 'linear-gradient(to right, red, yellow, green)', border: '1px solid rgba(255,255,255,0.2)' }} />
                           <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#9ca3af', marginTop: 2 }}>
                             <span>-1.0</span><span>0.0</span><span>+1.0</span>
                           </div>
                        </div>

                        {/* Min/Max Sliders */}
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#e2e8f0", marginBottom: 4 }}>
                            <span>Min Filter</span><span>{layer.params?.min ?? DEFAULT_MIN_NDVI}</span>
                          </div>
                          <input type="range" min="-1" max="1" step="0.05" value={layer.params?.min ?? DEFAULT_MIN_NDVI} onChange={(e) => updateLayerNdviParams(layer.id, { min: parseFloat(e.target.value) })} style={{ width: "100%", accentColor: "#ef4444", height: 4, cursor: 'pointer' }} />
                        </div>
                        <div style={{ marginBottom: 4 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#e2e8f0", marginBottom: 4 }}>
                            <span>Max Filter</span><span>{layer.params?.max ?? DEFAULT_MAX_NDVI}</span>
                          </div>
                          <input type="range" min="-1" max="1" step="0.05" value={layer.params?.max ?? DEFAULT_MAX_NDVI} onChange={(e) => updateLayerNdviParams(layer.id, { max: parseFloat(e.target.value) })} style={{ width: "100%", accentColor: "#10b981", height: 4, cursor: 'pointer' }} />
                        </div>
                        
                        <button onClick={() => updateLayerNdviParams(layer.id, { min: -1.0, max: 1.0 })} style={{ width: '100%', marginTop: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: 10, padding: '4px', borderRadius: 4, cursor: 'pointer' }}>Reset Range</button>
                       
                        <div style={{ fontSize: 12, marginTop: 12, padding: 8, background: "rgba(0,0,0,0.2)", borderRadius: 4 }}>
                          Hover NDVI Value:{" "}
                          <span style={{ fontWeight: 700, color: "#4ade80" }}>
                            
                            {ndviHoverVal === null ? "—" : Number(ndviHoverVal).toFixed(3)}
                          </span>
                        </div>
                        

                      </div>
                    )}
                  </div>
                ))}
                
                {(!data.ndvi || data.ndvi.length === 0) && (
                  <div style={{ padding: 10, textAlign: "center", color: "#64748b", fontSize: 12, fontStyle: "italic" }}>
                    No NDVI data available
                  </div>
                )}
              </>

              ) : key === "photos" ? (
                <>
                  {(data.photos || []).map((layer) => (
                    <LayerCard
                      key={layer.id}
                      bucket="photos"
                      layer={layer}
                      onToggle={(l) => toggleLayer("photos", l.id)}
                      onRemove={(l) => { if(l.id !== "ground_truth_photos_control") deletePhoto(l.id); }}
                      onZoom={(l) => emitMapEvent("map-zoom-layer", { id: l.id })}
                    />
                  ))}
                  {(!data.photos || data.photos.length === 0) && (
                    <div style={{ padding: 10, textAlign: "center", color: "#64748b", fontSize: 12, fontStyle: "italic" }}>
                      No photos found
                    </div>
                  )}
                </>

              ) : (
                <>
                  {(data[key] || []).map((layer) => (
                    <LayerCard
                      key={layer.id}
                      bucket={key}
                      layer={layer}
                      onToggle={(l) => toggleLayer(key, l.id)}
                      onOpacityChange={key.includes("raster") ? (val) => setOpacity(key, layer.id, val) : undefined}
                      onRemove={(l) => removeLayer(key, l.id)}
                      onZoom={(l) => emitMapEvent("map-zoom-layer", { id: l.id })}
                    />
                  ))}
                  {(!data[key] || data[key].length === 0) && (
                    <div style={{ padding: 10, textAlign: "center", color: "#64748b", fontSize: 12, fontStyle: "italic" }}>
                      No layers found
                    </div>
                  )}
                </>
              )}
            </SectionContainer>
          ))}
        </div>
      </aside>
    </>
  );
}

function formatSurveyLabel(raw) {
  if (!raw) return "—";
  try {
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-");
      return `${d}-${m}-${y}`;
    }
    const d = new Date(raw);
    if (!isNaN(d.getTime()))
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    return String(raw);
  } catch {
    return String(raw);
  }
}